import React from "react";
import { TopNav } from "@/components/TopNav";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { useLang } from "@/context/LanguageContext";
import { Diamond, ShieldCheck, Globe, Award, Mail } from "lucide-react";

const AboutPage = () => {
  const { lang } = useLang();

  const t = lang === "fr" ? {
    title: "À propos de GEM PRICE",
    tagline: "La référence des tarifs de pierres précieuses.",
    intro: "GEM PRICE est une plateforme indépendante dédiée à la transparence des prix des pierres précieuses. Nous publions une grille tarifaire certifiée par pays d'origine et par carat pour les saphirs, rubis et émeraudes, appuyée par les standards des laboratoires gemmologiques internationaux.",
    missionTitle: "Notre mission",
    missionBody: "Rendre l'expertise gemmologique accessible aux collectionneurs, joailliers, investisseurs et amateurs éclairés. Chaque fiche GEM PRICE croise l'origine, la qualité (AAA / AA / A), les traitements éventuels et les fourchettes de prix observées sur le marché.",
    valuesTitle: "Nos engagements",
    values: [
      { icon: ShieldCheck, title: "Indépendance", body: "Aucun conflit d'intérêt avec les mines ou les négociants. Nos données sont issues d'observations de marché et de sources publiques vérifiées." },
      { icon: Award, title: "Standards laboratoires", body: "Nous nous appuyons sur les référentiels du GIA, IGI, Gübelin, SSEF, AGL et GRS pour la classification et l'identification des pierres." },
      { icon: Globe, title: "Couverture mondiale", body: "Sri Lanka, Birmanie, Cachemire, Madagascar, Colombie, Zambie, Mozambique, Tanzanie… toutes les provenances majeures sont documentées." },
      { icon: Diamond, title: "Mise à jour continue", body: "Notre journal mensuel suit les découvertes minières, les évolutions du marché et les progrès des laboratoires." },
    ],
    contactTitle: "Contact",
    contactBody: "Une question, un projet, une demande d'expertise ? Notre équipe reste à votre disposition.",
    email: "gemprice@gemprice.org",
    footerNote: "GEM PRICE • Plateforme d'information gemmologique • gemprice.org",
  } : {
    title: "About GEM PRICE",
    tagline: "The reference for precious gemstone prices.",
    intro: "GEM PRICE is an independent platform dedicated to price transparency for precious gemstones. We publish a certified pricing grid by country of origin and per carat for sapphires, rubies and emeralds, backed by international gemmological laboratory standards.",
    missionTitle: "Our mission",
    missionBody: "Making gemmological expertise accessible to collectors, jewellers, investors and informed enthusiasts. Every GEM PRICE fact sheet cross-references origin, quality (AAA / AA / A), possible treatments and observed market price ranges.",
    valuesTitle: "Our commitments",
    values: [
      { icon: ShieldCheck, title: "Independence", body: "No conflict of interest with mines or dealers. Our data comes from market observations and verified public sources." },
      { icon: Award, title: "Laboratory standards", body: "We rely on GIA, IGI, Gübelin, SSEF, AGL and GRS reference frameworks for stone classification and identification." },
      { icon: Globe, title: "Global coverage", body: "Sri Lanka, Burma, Kashmir, Madagascar, Colombia, Zambia, Mozambique, Tanzania… all major origins are documented." },
      { icon: Diamond, title: "Continuous updates", body: "Our monthly journal tracks mining discoveries, market shifts and laboratory advances." },
    ],
    contactTitle: "Contact",
    contactBody: "A question, a project, an expertise request? Our team is here to help.",
    email: "gemprice@gemprice.org",
    footerNote: "GEM PRICE • Gemmological information platform • gemprice.org",
  };

  return (
    <div className="min-h-screen bg-[#050811] text-[#F8F5EE] flex flex-col">
      <SEO
        title={t.title}
        path="/about"
        description={t.intro.slice(0, 155)}
      />
      <TopNav />
      <main className="flex-1 px-6 md:px-14 py-14" data-testid="about-page">
        <div className="max-w-4xl mx-auto space-y-14">
          {/* Hero */}
          <header className="text-center space-y-3">
            <Diamond size={28} strokeWidth={1.2} className="text-[#C1A661] mx-auto" />
            <p className="text-[10px] tracking-[0.4em] uppercase text-[#C1A661]">GEM PRICE</p>
            <h1
              data-testid="about-title"
              className="font-serif font-light text-3xl md:text-5xl tracking-[0.06em] text-[#E2CFA4]"
            >
              {t.title}
            </h1>
            <p className="text-[13px] md:text-sm text-[#A1A7B3] font-light italic">
              {t.tagline}
            </p>
          </header>

          {/* Intro */}
          <section className="border border-[#C1A661]/20 rounded-sm p-6 md:p-8 bg-[#080C17]">
            <p className="text-[#D8DBE2] font-light leading-relaxed">{t.intro}</p>
          </section>

          {/* Mission */}
          <section className="space-y-4">
            <h2 className="font-serif text-2xl md:text-3xl text-[#E2CFA4] font-light">{t.missionTitle}</h2>
            <p className="text-[#A1A7B3] font-light leading-relaxed">{t.missionBody}</p>
          </section>

          {/* Values */}
          <section className="space-y-6">
            <h2 className="font-serif text-2xl md:text-3xl text-[#E2CFA4] font-light">{t.valuesTitle}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {t.values.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="border border-[#C1A661]/20 rounded-sm p-5 bg-[#080C17] flex gap-4"
                  data-testid={`about-value-${title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon size={22} strokeWidth={1.3} className="text-[#C1A661] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[11px] tracking-[0.25em] uppercase text-[#C1A661] font-medium mb-1">
                      {title}
                    </div>
                    <p className="text-[13px] text-[#D8DBE2] font-light leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="border border-[#C1A661]/30 rounded-sm p-6 md:p-8 bg-[#080C17] text-center space-y-3">
            <Mail size={20} strokeWidth={1.3} className="text-[#C1A661] mx-auto" />
            <h2 className="font-serif text-xl md:text-2xl text-[#E2CFA4] font-light">{t.contactTitle}</h2>
            <p className="text-[13px] text-[#A1A7B3] font-light">{t.contactBody}</p>
            <a
              href={`mailto:${t.email}`}
              data-testid="about-email-link"
              className="inline-block text-[#E2CFA4] hover:text-[#F8F5EE] transition-colors"
            >
              {t.email}
            </a>
          </section>

          <p className="text-center text-[10px] tracking-[0.3em] uppercase text-[#8A8F99]">
            {t.footerNote}
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
